import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import OfferCard from "@/components/OfferCard";
import { MapPin, Store, Tag, ArrowRight, Handshake, ChevronLeft, Building2 } from "lucide-react";
import { FounderPriceInline } from "@/components/FounderPriceBlock";

export default function CityPage() {
  const { citySlug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setData(null); setErr("");
    api.get(`/public/ville/${citySlug}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Commune introuvable."));
  }, [citySlug]);

  const title = useMemo(() => data ? `Bons plans à ${data.name} (28) | Réductions et commerces locaux | PROXIreduc-28` : "", [data]);

  useEffect(() => {
    if (!data) return;
    document.title = title;
    const desc = `Tous les bons plans, réductions et avantages à ${data.name} en Eure-et-Loir : ${data.offers_total} offre(s) locale(s) et ${data.businesses_total} commerce(s) partenaire(s). Pôle ${data.pole.center}.`;
    let md = document.querySelector('meta[name="description"]');
    if (!md) { md = document.createElement("meta"); md.name = "description"; document.head.appendChild(md); }
    md.setAttribute("content", desc);
    let cn = document.querySelector('link[rel="canonical"]');
    if (!cn) { cn = document.createElement("link"); cn.setAttribute("rel", "canonical"); document.head.appendChild(cn); }
    cn.setAttribute("href", `${window.location.origin}/bons-plans/ville/${data.slug}`);

    const origin = window.location.origin;
    const ld = [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", position: 1, name: "Accueil", item: `${origin}/` },
          { "@type": "ListItem", position: 2, name: "Villes", item: `${origin}/villes` },
          { "@type": "ListItem", position: 3, name: data.pole.name, item: `${origin}/pole/${data.pole.id}` },
          { "@type": "ListItem", position: 4, name: data.name, item: `${origin}/bons-plans/ville/${data.slug}` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": `Commerces partenaires à ${data.name}`,
        "itemListElement": (data.businesses || []).slice(0, 20).map((b, i) => ({
          "@type": "ListItem", position: i + 1,
          item: {
            "@type": "LocalBusiness", name: b.name,
            address: { "@type": "PostalAddress", addressLocality: b.city || data.name, addressRegion: "Centre-Val de Loire", addressCountry: "FR" },
            ...(b.logo ? { image: b.logo } : {}),
            ...(b.phone ? { telephone: b.phone } : {}),
          },
        })),
      },
    ];
    let script = document.getElementById("city-jsonld");
    if (!script) {
      script = document.createElement("script");
      script.id = "city-jsonld"; script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ld);
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) { canon = document.createElement("link"); canon.rel = "canonical"; document.head.appendChild(canon); }
    canon.href = `${origin}/bons-plans/ville/${data.slug}`;
  }, [data, title]);

  if (err) {
    return (
      <div className="max-w-2xl mx-auto py-20 px-4 text-center" data-testid="city-error">
        <div className="text-2xl font-heading font-extrabold text-rose-700">Commune introuvable</div>
        <p className="mt-2 text-slate-600">{err}</p>
        <Link to="/villes" className="mt-4 inline-block text-[#E05638] font-semibold">← Voir toutes les communes</Link>
      </div>
    );
  }
  if (!data) return <div className="max-w-2xl mx-auto py-20 text-center text-slate-500">Chargement…</div>;

  return (
    <div data-testid={`city-page-${data.slug}`}>
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-[#E05638] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <div className="mb-4">
            <Link to={`/pole/${data.pole.id}`} className="inline-flex items-center gap-1 text-white/80 hover:text-white text-sm" data-testid="city-back-pole">
              <ChevronLeft size={14}/> {data.pole.name}
            </Link>
          </div>
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 border border-white/25 text-white text-xs font-bold uppercase tracking-widest">
            <MapPin size={12}/> {data.name} · Eure-et-Loir (28)
          </span>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight max-w-3xl" data-testid="city-h1">
            Bons plans et réductions à <span className="text-amber-300">{data.name}</span>
          </h1>
          <p className="mt-3 text-white/85 max-w-2xl">
            {data.offers_total > 0
              ? `${data.offers_total} bon${data.offers_total > 1 ? "s" : ""} plan${data.offers_total > 1 ? "s" : ""} proposé${data.offers_total > 1 ? "s" : ""} par les commerces de ${data.name}`
              : `Les premiers commerces de ${data.name} rejoignent le réseau`}
            {data.department_total > 0 && ` · ${data.department_total} offre${data.department_total > 1 ? "s" : ""} valable${data.department_total > 1 ? "s" : ""} dans tout le département`}.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to={`/bons-plans?city=${encodeURIComponent(data.name)}`}>
              <Button className="btn-shine text-white h-11 px-5" data-testid="city-cta-deals">
                <Tag size={16} className="mr-2"/> Voir les offres <ArrowRight size={14} className="ml-1"/>
              </Button>
            </Link>
            <Link to="/inscription">
              <Button variant="outline" className="bg-white/10 border-white/40 text-white hover:bg-white/20 h-11 px-5" data-testid="city-cta-register">
                Créer mon compte gratuit
              </Button>
            </Link>
          </div>
          <div className="mt-6 flex flex-wrap gap-4 text-sm text-white/85">
            <span className="inline-flex items-center gap-1.5"><Tag size={14}/> {data.offers_total} offres locales</span>
            <span className="inline-flex items-center gap-1.5"><Store size={14}/> {data.businesses_total} commerces partenaires</span>
            <span className="inline-flex items-center gap-1.5"><MapPin size={14}/> Pôle {data.pole.center}</span>
          </div>
        </div>
      </section>

      {data.offers.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="city-offers">
          <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900">
            Les bons plans à {data.name}
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {data.offers.slice(0, 12).map((o) => <OfferCard key={o.id} offer={o} />)}
          </div>
        </section>
      )}

      {data.department_offers?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="city-dept-offers">
          <h2 className="font-heading text-xl md:text-2xl font-extrabold text-slate-900">
            Offres valables dans tout l'Eure-et-Loir
          </h2>
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {data.department_offers.slice(0, 6).map((o) => <OfferCard key={o.id} offer={o} />)}
          </div>
        </section>
      )}

      {data.businesses.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="city-businesses">
          <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900">
            Commerces partenaires à {data.name}
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.businesses.map((b) => (
              <Link key={b.id} to={`/commerce/${b.id}`} className="group bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-3 hover:border-[#E05638] transition-colors" data-testid={`city-business-${b.id}`}>
                <div className="w-11 h-11 rounded-xl bg-orange-50 text-[#E05638] grid place-items-center shrink-0 overflow-hidden">
                  {b.logo ? <img src={b.logo} alt={b.name} className="w-full h-full object-cover"/> : <Store size={18}/>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-heading font-bold text-slate-900 truncate">{b.name}</div>
                  <div className="text-[11px] text-slate-500 inline-flex items-center gap-1"><MapPin size={10}/> {b.city}</div>
                </div>
                <ArrowRight size={14} className="text-slate-400 group-hover:text-[#E05638] shrink-0"/>
              </Link>
            ))}
          </div>
        </section>
      )}

      {data.nearby?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="city-nearby">
          <h2 className="font-heading text-2xl font-extrabold text-slate-900">Bons plans autour de {data.name}</h2>
          <p className="mt-1 text-sm text-slate-500">Les autres communes du pôle {data.pole.center}.</p>
          <div className="mt-5 flex flex-wrap gap-2">
            {data.nearby.map((c) => (
              <Link key={c.slug} to={`/bons-plans/ville/${c.slug}`} data-testid={`city-nearby-${c.slug}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-sm text-slate-700 hover:border-[#E05638] hover:text-[#E05638] transition-colors">
                <MapPin size={12}/> {c.name}
                <span className="text-[11px] text-slate-400">{c.offers}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="rounded-3xl bg-gradient-to-br from-emerald-900 via-slate-900 to-emerald-950 text-white p-8 md:p-12 grid md:grid-cols-2 gap-6 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-amber-300 text-xs font-bold uppercase tracking-widest">
              <Building2 size={12}/> Commerçant à {data.name} ?
            </div>
            <h2 className="mt-3 font-heading text-2xl md:text-3xl font-extrabold">
              Faites connaître votre commerce à {data.name}
            </h2>
            <p className="mt-2 text-white/85 text-sm">
              Partenaire Fondateur : <FounderPriceInline dark /> · offres illimitées · pas de commission sur les ventes.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <Link to="/devenir-partenaire">
              <Button className="btn-shine text-white h-12 px-6" data-testid="city-partner-cta">
                <Handshake size={16} className="mr-2"/> Devenir partenaire <ArrowRight size={14} className="ml-1"/>
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
